import React, { useRef } from "react";

/**
 * 6 ank ka OTP input — signup verify aur forgot-password dono jagah wahi.
 * Controlled hai: `value` hamesha 0-6 digit ka string.
 * Paste, backspace aur arrow keys sambhalta hai; mobile par numeric keypad.
 */
export const OtpBoxes: React.FC<{
  value: string;
  onChange: (next: string) => void;
  onComplete?: (full: string) => void;
  disabled?: boolean;
  length?: number;
}> = ({ value, onChange, onComplete, disabled = false, length = 6 }) => {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");

  const push = (next: string) => {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length && onComplete) onComplete(clean);
  };

  const setAt = (index: number, digit: string) => {
    const arr = Array.from({ length }, (_, i) => value[i] || "");
    arr[index] = digit;
    push(arr.join("").replace(/\s/g, ""));
  };

  const focusAt = (index: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, index))];
    if (el) {
      el.focus();
      el.select();
    }
  };

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-2.5" dir="ltr">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          aria-label={`OTP ${i + 1}`}
          value={d}
          onChange={(e) => {
            const only = e.target.value.replace(/\D/g, "");
            if (!only) {
              setAt(i, "");
              return;
            }
            if (only.length > 1) {
              push((value.slice(0, i) + only).slice(0, length));
              focusAt(i + only.length);
              return;
            }
            setAt(i, only);
            if (i < length - 1) focusAt(i + 1);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              e.preventDefault();
              if (digits[i]) {
                setAt(i, "");
              } else if (i > 0) {
                const arr = Array.from({ length }, (_, k) => value[k] || "");
                arr[i - 1] = "";
                push(arr.join(""));
                focusAt(i - 1);
              }
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              focusAt(i - 1);
            } else if (e.key === "ArrowRight") {
              e.preventDefault();
              focusAt(i + 1);
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
            if (!text) return;
            push(text.slice(0, length));
            focusAt(Math.min(length - 1, text.length));
          }}
          onFocus={(e) => e.target.select()}
          className="ci-num h-[52px] w-[44px] rounded-md border border-[#cfc9bf] bg-white text-center text-[22px] font-bold text-ci-ink outline-none transition focus:border-ci-navy focus:ring-2 focus:ring-[#182A70]/15 disabled:opacity-60 sm:h-[58px] sm:w-[50px]"
        />
      ))}
    </div>
  );
};

export default OtpBoxes;
